import {Dialog, Transition} from "@headlessui/react";
import {BlackButton} from "../../Buttons/BlackButton";
import {Fragment, useEffect, useState} from "react";
import {PrimaryButton} from "../../Buttons/PrimaryButton";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import {SelectInput} from "../../Form/SelectInput";
import {NumberInput} from "../../Form/NumberInput";
import {DateTimePickerInput} from "../../Form/DateTimePickerInput";
import {ControlledSwitch} from "../../Form/ControlledSwitch";
import {useBoundStore} from "../../../Store/store";
import dayjs from "dayjs";
import {isMyCcpSubdomain} from "../../../Utils/SubdomainHelper";
import {useTranslation} from "react-i18next";

export const ExceptionRateDialog = (props) => {
    const {
        open,
        onClose,
        freelancerId,
        exceptionRate = null,
    } = props;

    const {t} = useTranslation();
    const [loading, setLoading] = useState(false);
    const [loadingBranches, setLoadingBranches] = useState(false);
    const [branches, setBranches] = useState([]);
    const [formInitialized, setFormInitialized] = useState(false);

    const getMyBranchesNotPaginated = useBoundStore((state) => state.getMyBranchesNotPaginated);
    const branchesNotPaginated = useBoundStore((state) => state.branchesNotPaginated);
    const createExceptionRate = useBoundStore((state) => state.createExceptionRate);
    const updateExceptionRate = useBoundStore((state) => state.updateExceptionRate);

    const schema = yup.object({
        branch_id: yup.string().required(t('exception_rates.branch_required')),
        internal_rate: yup.number().required(t('exception_rates.internal_rate_required')).min(0, t('exception_rates.internal_rate_min')),
        start_date: yup.date().required(t('exception_rates.start_date_required')),
        no_end_date: yup.boolean(),
        end_date: yup.date().nullable().when('no_end_date', {
            is: false,
            then: (schema) => schema.required(t('exception_rates.end_date_required')).min(yup.ref('start_date'), t('exception_rates.end_date_after_start')),
            otherwise: (schema) => schema.nullable()
        }),
    }).required();

    const {
        register,
        handleSubmit,
        formState: {errors},
        setValue,
        control,
        reset,
        watch,
        getValues
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            branch_id: null,
            internal_rate: null,
            start_date: null,
            no_end_date: false,
            end_date: null,
        }
    });

    useEffect(() => {
        if (open) {
            const fetchBranches = async () => {
                setLoadingBranches(true);
                await getMyBranchesNotPaginated();
                setLoadingBranches(false);
            };

            fetchBranches();
        }
    }, [open, getMyBranchesNotPaginated]);

    useEffect(() => {
        if (branchesNotPaginated) {
            setBranches(branchesNotPaginated);
        }
    }, [branchesNotPaginated]);

    useEffect(() => {
        if (exceptionRate) {
            reset({
                branch_id: exceptionRate.branch_id?.toString() || null,
                internal_rate: exceptionRate.internal_rate || null,
                start_date: exceptionRate.start_date ? dayjs(exceptionRate.start_date).toDate() : null,
                no_end_date: !exceptionRate?.end_date,
                end_date: exceptionRate.end_date ? dayjs(exceptionRate.end_date).toDate() : null,
            });
        } else {
            reset({
                branch_id: null,
                internal_rate: null,
                start_date: null,
                no_end_date: false,
                end_date: null,
            });
        }
    }, [exceptionRate, reset]);

    const onSubmit = async (data) => {
        setLoading(true);

        const internalRateValue = parseFloat(data.internal_rate);
        const calculatedExternalRate = isMyCcpSubdomain() ? internalRateValue : internalRateValue * 1.18;

        const formattedData = {
            branch_id: parseInt(data.branch_id),
            internal_rate: internalRateValue,
            external_rate: calculatedExternalRate,
            start_date: dayjs(data.start_date).format('YYYY-MM-DD'),
            end_date: data.no_end_date ? null : dayjs(data.end_date).format('YYYY-MM-DD'),
        };

        try {
            if (exceptionRate) {
                await updateExceptionRate(freelancerId, exceptionRate.id, formattedData);
            } else {
                await createExceptionRate(freelancerId, formattedData);
            }
            reset();
            onClose();
        } catch (error) {
            console.error('Error saving exception rate:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const branchOptions = branches.map(branch => ({
        value: branch.id.toString(),
        label: branch.company_name || t('exception_rates.unnamed'),
        id: branch.id
    }));

    const internalRate = watch('internal_rate');
    const noEndDate = watch('no_end_date');
    const calculatedExternalRate = !isMyCcpSubdomain() && internalRate ? (Math.round(parseFloat(internalRate) * 1.18 * 100) / 100)
        .toFixed(2)
        .replace('.', ',') : null;

    return (
        <>
            <Transition appear show={open} as={Fragment}>
                <Dialog as="div" className="relative z-10" onClose={handleClose}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/50"/>
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel
                                    className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                    <Dialog.Title
                                        as="h3"
                                        className="text-lg font-medium leading-6 text-gray-900"
                                    >
                                        {exceptionRate ? t('exception_rates.edit_title') : t('exception_rates.new_title')}
                                    </Dialog.Title>
                                    <div className="mt-4">
                                        <p className="text-sm text-gray-500 mb-4">
                                            {exceptionRate
                                                ? t('exception_rates.edit_description')
                                                : t('exception_rates.new_description')
                                            }
                                        </p>

                                        {loadingBranches ? (
                                            <div className="flex justify-center items-center py-8">
                                                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                                            </div>
                                        ) : (
                                            <form onSubmit={handleSubmit(onSubmit)}>
                                                <div className="space-y-4">
                                                    <SelectInput
                                                        name="branch_id"
                                                        control={control}
                                                        setValue={setValue}
                                                        translationIndex={t('exception_rates.branch')}
                                                        options={branchOptions}
                                                        errors={errors}
                                                        clearable={true}
                                                    />

                                                    <div>
                                                        <NumberInput
                                                            name="internal_rate"
                                                            register={register}
                                                            step={0.01}
                                                            translationIndex={t('exception_rates.internal_rate')}
                                                            placeholder="0.00"
                                                            errors={errors}
                                                            suffix="€"
                                                        />
                                                        {calculatedExternalRate && (
                                                            <p className="text-sm text-gray-600 mt-1">
                                                                <strong>{t('exception_rates.external_rate')}:</strong> €{calculatedExternalRate}
                                                            </p>
                                                        )}
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-4">
                                                        <DateTimePickerInput
                                                            name="start_date"
                                                            control={control}
                                                            setValue={setValue}
                                                            format={'DD/MM/YYYY'}
                                                            translationIndex={t('exception_rates.start_date')}
                                                            errors={errors}
                                                            disableTime={true}
                                                        />
                                                        <div>
                                                            <DateTimePickerInput
                                                                name="end_date"
                                                                control={control}
                                                                setValue={setValue}
                                                                format={'DD/MM/YYYY'}
                                                                translationIndex={t('exception_rates.end_date')}
                                                                errors={errors}
                                                                disableTime={true}
                                                                disabled={noEndDate}
                                                            />
                                                            <div className="mt-2">
                                                                <ControlledSwitch
                                                                    name="no_end_date"
                                                                    control={control}
                                                                    setValue={setValue}
                                                                    getValues={getValues}
                                                                    translationIndex="general.fields.no_end_date"
                                                                    errors={errors}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className={'flex justify-end'}>
                                                    <div className="mt-6 grid grid-cols-2 w-fit gap-4">
                                                        <div>
                                                            <BlackButton outlined={true} onClick={handleClose} disabled={loading}>
                                                                {t('general.actions.cancel')}
                                                            </BlackButton>
                                                        </div>
                                                        <PrimaryButton
                                                            type="submit"
                                                            disabled={loading}
                                                        >
                                                            {loading ? (
                                                                <div className="flex items-center">
                                                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                    </svg>
                                                                    {t('general.messages.processing')}
                                                                </div>
                                                            ) : (exceptionRate ? t('exception_rates.update') : t('exception_rates.create'))}
                                                        </PrimaryButton>
                                                    </div>
                                                </div>
                                            </form>
                                        )}
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </>
    )
}

